import React from 'react'
import { Box, Grid, makeStyles } from '@material-ui/core'
import { useForm } from 'react-final-form'
import { getIn } from 'final-form'
import MyTextfield from '../../../../control/textfield/textfield'
import MyCheckbox from '../../../../control/checkbox/checkbox'
import MyButton from '../../../../control/button/button'
import { PersonOption } from '../agendaTypes'
import { validateEmail } from '../../../../utilities/validate'
import useTranslate from '../../../../i18n/useTranslate'
import MyAutocomplete from '../../../../control/autocomplete/autocomplete'

type PersonInfo = { email?: string; fullnameTh?: string; fullnameEn?: string }

const MemberRowInternal: React.FC<{
    namePrefix: string
    onRemove?: () => void
    defaultNotify?: boolean
    requiredEmail?: boolean
    options: PersonOption[]
    idToEmail: Record<string, string>
    idToPerson: Record<string, PersonInfo>
    loading?: boolean
    disableRemove?: boolean
    defaultPersonValue?: any
    canEdit?: boolean
}> = ({
    namePrefix,
    onRemove,
    defaultNotify = true,
    requiredEmail = false,
    options,
    idToEmail,
    idToPerson,
    loading,
    disableRemove,
    defaultPersonValue,
    canEdit = true,
}) => {
    const classes = useStyles()
    const t = useTranslate()
    const form = useForm()

    const extractSelectedId = React.useCallback((val: any): string | undefined => {
        const v = Array.isArray(val) ? val[0] : val
        if (v == null) return
        if (typeof v === 'string' || typeof v === 'number') return String(v)
        if (typeof v === 'object') {
            if (v.value != null) return String(v.value)
            if (v.id != null) return String(v.id)
        }
        return
    }, [])

    const setIfChanged = React.useCallback(
        (path: string, next: any) => {
            const cur = getIn(form.getState().values, path)
            if (cur !== next) form.change(path, next)
        },
        [form],
    )

    const applyPersonFields = React.useCallback(
        (val: any) => {
            const id = extractSelectedId(val)

            const person = id ? idToPerson[id] : undefined
            const email = id ? (idToEmail[id] ?? person?.email) : undefined

            const fullnameTh = id ? (idToPerson[id]?.fullnameTh ?? '') : ''
            const fullnameEn = id ? (idToPerson[id]?.fullnameEn ?? '') : ''

            form.batch(() => {
                setIfChanged(`${namePrefix}.email`, email || undefined)
                setIfChanged(`${namePrefix}.fullnameTh`, fullnameTh)
                setIfChanged(`${namePrefix}.fullnameEn`, fullnameEn)
                setIfChanged(`${namePrefix}.userId`, id)
            })
        },
        [extractSelectedId, form, idToEmail, idToPerson, namePrefix, setIfChanged],
    )

    const resolvePersonLabel = React.useCallback(
        (id?: string) => {
            if (!id) return ''
            const person = idToPerson[id]
            const fallbackName =
                defaultPersonValue?.fullnameTh ??
                defaultPersonValue?.fullnameEn ??
                defaultPersonValue?.name ??
                defaultPersonValue?.label ??
                ''

            return person?.fullnameTh || person?.fullnameEn || fallbackName || id
        },
        [defaultPersonValue, idToPerson],
    )

    const getAutocompleteValue = React.useCallback(() => {
        const current = getIn(form.getState().values, `${namePrefix}.user`) ?? null
        const id = extractSelectedId(current)
        if (!current) return null

        if (typeof current === 'string' || typeof current === 'number') {
            const currentId = String(current)
            return { value: currentId, label: resolvePersonLabel(currentId) }
        }

        if (typeof current === 'object') {
            const currentId = id ?? extractSelectedId(defaultPersonValue)
            return {
                ...current,
                value: current.value ?? currentId,
                label: current.label || resolvePersonLabel(currentId),
            }
        }

        return current
    }, [defaultPersonValue, extractSelectedId, form, namePrefix, resolvePersonLabel])

    React.useEffect(() => {
        const current = getIn(form.getState().values, `${namePrefix}.user`) ?? null
        const currentId = extractSelectedId(current) ?? extractSelectedId(defaultPersonValue)
        if (!currentId) return

        const nextValue = { value: currentId, label: resolvePersonLabel(currentId) }
        const currentLabel = typeof current === 'object' && current ? current.label : undefined
        const currentValueId = extractSelectedId(current)

        if (currentValueId !== currentId || currentLabel !== nextValue.label) {
            form.change(`${namePrefix}.user`, nextValue)
        }
    }, [defaultPersonValue, extractSelectedId, form, namePrefix, resolvePersonLabel])

    return (
        <Box className={classes.rowCard}>
            <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                    <MyAutocomplete
                        name={`${namePrefix}.user`}
                        options={options}
                        materialInputProps={{
                            label: t.get('UM.SETTING_AGENDA.PARTICIPANTS.FULLNAME'),
                            placeholder: t.get('UM.SETTING_AGENDA.PARTICIPANTS.FULLNAME'),
                            disabled: !canEdit,
                        }}
                        required
                        onChangeValue={applyPersonFields}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <MyTextfield
                        name={`${namePrefix}.email`}
                        materialInputProps={{
                            label: t.get('UM.SETTING_AGENDA.PARTICIPANTS.EMAIL'),
                            placeholder: t.get('UM.SETTING_AGENDA.PARTICIPANTS.EMAIL'),
                            disabled: !canEdit,
                        }}
                        required={requiredEmail}
                        validates={[validateEmail(t.get('MESSAGE.M017'))]}
                    />
                </Grid>

                <Grid item xs={12}>
                    <div className={classes.rightControls}>
                        <MyCheckbox
                            name={`${namePrefix}.sendMail`}
                            label={t.get('UM.SETTING_AGENDA.PARTICIPANTS.SEND_MAIL')}
                            disabled={!canEdit}
                        />
                        <MyButton
                            onClick={onRemove}
                            color="secondary"
                            style={{ minWidth: 80 }}
                            disabled={disableRemove || !canEdit}
                        >
                            {t.get('BUTTON.DELETE')}
                        </MyButton>
                    </div>
                </Grid>
            </Grid>
        </Box>
    )
}

const useStyles = makeStyles((theme) => ({
    rowCard: {
        border: '1px solid #E5E7EB',
        borderRadius: 8,
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    rightControls: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        height: '100%',
    },
}))

export default React.memo(MemberRowInternal)
